import { useNavigation } from '@refinedev/core';
import { List, useTable } from '@refinedev/antd';
import { Table, Tag, Button, Badge } from 'antd';
import { EyeOutlined, AlertOutlined } from '@ant-design/icons';
import { useEffect, useRef } from 'react';
import { playEmergencySound } from '../../lib/emergency-sound';
import { t } from '../../i18n';

interface TicketMessage {
  id: number;
  content: string;
  senderType: 'CLIENT' | 'DRIVER' | 'SUPPORT' | 'SYSTEM';
  createdAt: string;
}

interface EmergencyTicket {
  id: number;
  subject: string;
  status: 'OPEN' | 'IN_PROGRESS' | 'RESOLVED' | 'CLOSED';
  isEmergency: boolean;
  createdAt: string;
  messages: TicketMessage[];
  user: {
    fullName: string;
    phone: string;
  };
}

export const EmergencyChatList = () => {
  const { show } = useNavigation();
  const prevSnapshotRef = useRef<string>('');

  const { tableProps, tableQueryResult } = useTable<EmergencyTicket>({
    resource: 'admin/tickets',
    syncWithLocation: true,
    filters: {
      permanent: [
        { field: 'isEmergency', operator: 'eq', value: true },
      ],
    },
    sorters: {
      initial: [{ field: 'createdAt', order: 'desc' }],
    },
  });

  const { refetch } = tableQueryResult;

  // Auto-refresh every 10 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      refetch();
    }, 10000);
    return () => clearInterval(interval);
  }, [refetch]);

  // Detect new client messages across all tickets and play emergency sound
  useEffect(() => {
    const tickets = (tableProps.dataSource || []) as EmergencyTicket[];
    // Build snapshot of latest non-support message IDs per ticket
    const snapshot = tickets
      .map((t) => {
        const lastMsg = t.messages?.[0];
        return lastMsg ? `${t.id}:${lastMsg.id}:${lastMsg.senderType}` : `${t.id}:none`;
      })
      .join('|');

    const prev = prevSnapshotRef.current;
    if (prev && snapshot !== prev) {
      // Check if any ticket has a new non-SUPPORT message
      const prevMap = new Map(
        prev.split('|').map((entry) => {
          const [tid, mid] = entry.split(':');
          return [tid, mid];
        }),
      );
      for (const t of tickets) {
        const lastMsg = t.messages?.[0];
        if (!lastMsg) continue;
        const prevMsgId = prevMap.get(String(t.id));
        if (
          prevMsgId !== String(lastMsg.id) &&
          lastMsg.senderType !== 'SUPPORT'
        ) {
          playEmergencySound();
          break;
        }
      }
    }
    prevSnapshotRef.current = snapshot;
  }, [tableProps.dataSource]);

  const statusLabels: Record<string, string> = {
    OPEN: t('support.statusOpen'),
    IN_PROGRESS: t('support.statusInProgress'),
    RESOLVED: t('support.statusResolved'),
    CLOSED: t('support.statusClosed'),
  };

  const statusColors: Record<string, string> = {
    OPEN: 'red',
    IN_PROGRESS: 'orange',
    RESOLVED: 'green',
    CLOSED: 'default',
  };

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 80,
      render: (id: number) => (
        <Badge dot color="red">
          <span style={{ fontWeight: 600 }}>#{id}</span>
        </Badge>
      ),
    },
    {
      title: t('support.ticketUser'),
      dataIndex: 'user',
      key: 'user',
      render: (user: EmergencyTicket['user']) => (
        <div>
          <div style={{ fontWeight: 500 }}>{user?.fullName || t('common.noName')}</div>
          <div style={{ color: '#E74C3C', fontSize: 13, fontWeight: 500 }}>
            {user?.phone}
          </div>
        </div>
      ),
    },
    {
      title: t('emergency.lastMessage'),
      dataIndex: 'messages',
      key: 'lastMessage',
      ellipsis: true,
      render: (messages: TicketMessage[]) => {
        const lastMsg = messages?.[0];
        return (
          <span style={{ color: '#555' }}>{lastMsg?.content || '-'}</span>
        );
      },
    },
    {
      title: t('common.status'),
      dataIndex: 'status',
      key: 'status',
      width: 120,
      render: (status: string) => (
        <Tag color={statusColors[status]}>{statusLabels[status] || status}</Tag>
      ),
    },
    {
      title: t('common.date'),
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 160,
      render: (date: string) =>
        new Date(date).toLocaleString('uz-UZ', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
        }),
    },
    {
      title: t('common.actions'),
      key: 'actions',
      width: 80,
      render: (_: unknown, record: EmergencyTicket) => (
        <Button
          type="primary"
          danger
          icon={<EyeOutlined />}
          size="small"
          onClick={() => show('emergency', record.id)}
        />
      ),
    },
  ];

  return (
    <List
      title={
        <span>
          <AlertOutlined style={{ color: '#E74C3C', marginRight: 8 }} />
          {t('emergency.title')}
        </span>
      }
    >
      <Table {...tableProps} columns={columns} rowKey="id" />
    </List>
  );
};
